// src/components/ProtectedRoute.tsx
import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { UserRole } from '../types/chat';
import { isGuestProfileIncomplete } from '../shared/validators';

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: UserRole[];
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, allowedRoles }) => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          bgcolor: 'background.default',
          gap: 2,
        }}
      >
        <CircularProgress size={60} thickness={4} />
        <Typography variant="h6" color="text.secondary">
          Loading...
        </Typography>
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Auto-redirect guests with incomplete profile to CompleteGuestInfoPage
  // (except if already on these exempt pages)
  if (user && user.role === UserRole.GUEST) {
    const exemptPaths = ['/complete-guest-info', '/guest-onboarding', '/add-reservation-note', '/add-check-in-time'];
    const isExemptPath = exemptPaths.some(path => location.pathname.startsWith(path));
    
    if (!isExemptPath && isGuestProfileIncomplete(user)) {
      const params = new URLSearchParams();
      if (user.hotelId) params.append('hotelId', user.hotelId);
      if (user.reservationId) params.append('reservationId', user.reservationId);
      if (user.profileId) params.append('profileId', user.profileId);
      if (user.lastName) params.append('surname', user.lastName);

      return <Navigate to={`/complete-guest-info?${params.toString()}`} replace />;
    }
  }

  // Check if user has required role
  if (allowedRoles && user) {
    const hasRequiredRole = allowedRoles.includes(user.role);
    if (!hasRequiredRole) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
            bgcolor: 'background.default',
            gap: 2,
          }}
        >
          <Typography variant="h4" color="error">
            Access Denied
          </Typography>
          <Typography variant="body1" color="text.secondary">
            You don't have permission to access this page.
          </Typography>
        </Box>
      );
    }
  }

  return <>{children}</>;
};

export default ProtectedRoute;